import { useCallback, useEffect, useRef, useState } from 'react';
import { Portal, Menu, MenuButton } from '@cdl/heartbeat-react';
import { ContextMenuProps } from './context-menu.types';

export function ContextMenu<T extends HTMLElement = HTMLElement>(
  props: ContextMenuProps<T>,
) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [position, setPosition] = useState<[number, number]>([0, 0]);
  const targetRef = useRef<T>(null);

  const onCloseHandler = useCallback(() => {
    props.menuProps?.onClose?.();
    setIsOpen((value) => {
      if (value === true) {
        setTimeout(() => {
          setIsRendered(false);
        }, 500);
        return false;
      }
      return value;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.menuProps?.onClose, setIsOpen]);

  useEffect(() => {
    const targetDom = targetRef.current;
    if (targetDom == null) {
      return;
    }

    const handleContextMenu = (event: MouseEvent) => {
      if (targetDom.contains(event.target as Node)) {
        setIsRendered(true);
        setTimeout(() => {
          setIsOpen(true);
        });
        setPosition([event.pageX, event.pageY]);
        event.preventDefault();
      } else {
        onCloseHandler();
      }
    };

    window.addEventListener('contextmenu', handleContextMenu);
    return () => {
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [onCloseHandler]);

  useEffect(() => {
    const handleClose = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseHandler();
      }
    };

    window.addEventListener('keydown', handleClose);
    return () => {
      window.removeEventListener('keydown', handleClose);
    };
  }, [onCloseHandler]);

  return (
    <>
      {props.children(targetRef)}
      {isRendered && (
        <Portal {...props.portalProps}>
          <Menu
            isOpen={isOpen}
            {...props.menuProps}
            onClose={onCloseHandler}
            variant='virtual'
          >
            <MenuButton
              position='absolute'
              left={position[0]}
              top={position[1]}
              {...props.menuButtonProps}
            />
            {props.renderMenu({ onClose: () => onCloseHandler() })}
          </Menu>
        </Portal>
      )}
    </>
  );
}
